import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Loader2, Send, Search, MessageCircle, User, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * TeacherMessages — Instagram-style DM inbox for the teacher.
 * Left: conversation list (every approved student) with unread badges.
 * Right: the selected thread + composer. Polls every 5s.
 */
const TeacherMessages = ({ apiUrl, token, compact = false }) => {
  const [convos, setConvos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState('');
  const scrollRef = useRef(null);

  const authHeaders = useMemo(() => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  }), [token]);

  const fetchConvos = useCallback(async () => {
    try {
      const r = await fetch(`${apiUrl}/api/teacher/conversations`, { headers: authHeaders });
      if (r.ok) setConvos(await r.json());
    } catch { /* ignore */ }
    setLoading(false);
  }, [apiUrl, authHeaders]);

  const fetchThread = useCallback(async (sid) => {
    if (!sid) return;
    try {
      const r = await fetch(`${apiUrl}/api/teacher/conversations/${sid}`, { headers: authHeaders });
      if (r.ok) setMessages(await r.json());
    } catch { /* ignore */ }
  }, [apiUrl, authHeaders]);

  useEffect(() => { fetchConvos(); }, [fetchConvos]);

  // Poll: refresh conversation list + the open thread.
  useEffect(() => {
    const t = setInterval(() => {
      fetchConvos();
      if (activeId) fetchThread(activeId);
    }, 5000);
    return () => clearInterval(t);
  }, [fetchConvos, fetchThread, activeId]);

  useEffect(() => {
    if (activeId) fetchThread(activeId);
  }, [activeId, fetchThread]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const activeConvo = convos.find((c) => c.student_id === activeId);

  const send = async () => {
    const text = draft.trim();
    if (!text || !activeId) return;
    setSending(true);
    setDraft('');
    try {
      const r = await fetch(`${apiUrl}/api/teacher/conversations/${activeId}`, {
        method: 'POST', headers: authHeaders, body: JSON.stringify({ text }),
      });
      if (r.ok) {
        const m = await r.json();
        setMessages((prev) => [...prev, m]);
        fetchConvos();
      } else toast.error('فشل إرسال الرسالة');
    } catch { toast.error('حدث خطأ'); }
    setSending(false);
  };

  const deleteConversation = async () => {
    if (!activeId) return;
    if (!window.confirm(`حذف كامل المحادثة مع ${activeConvo?.student_name || 'الطالب'}؟ لا يمكن التراجع.`)) return;
    try {
      const r = await fetch(`${apiUrl}/api/teacher/conversations/${activeId}`, {
        method: 'DELETE', headers: authHeaders,
      });
      if (r.ok) {
        toast.success('تم حذف المحادثة');
        setMessages([]);
        setActiveId(null);
        fetchConvos();
      } else toast.error('فشل حذف المحادثة');
    } catch { toast.error('حدث خطأ'); }
  };

  const filtered = convos.filter((c) =>
    !search.trim() || (c.student_name || '').toLowerCase().includes(search.trim().toLowerCase()));

  return (
    <div className={`flex border border-[#E8DFCF] overflow-hidden bg-white ${compact ? 'h-full rounded-none border-0' : 'h-[70vh] rounded-2xl'}`}>
      {/* Conversation list */}
      <div className={`w-full md:w-80 border-l border-[#E8DFCF] flex flex-col ${activeId ? 'hidden md:flex' : 'flex'}`}>
        <div className="p-3 border-b border-[#E8DFCF]">
          <div className="relative">
            <Search className="w-4 h-4 text-[#412402]/40 absolute right-2 top-1/2 -translate-y-1/2" />
            <Input placeholder="بحث عن طالب..." value={search} onChange={(e) => setSearch(e.target.value)} className="pr-8 h-9" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin text-amber-600 mx-auto" /></div>
          ) : filtered.length === 0 ? (
            <p className="text-center text-sm text-[#412402]/50 py-10">لا يوجد طلاب</p>
          ) : filtered.map((c) => (
            <button
              key={c.student_id}
              onClick={() => setActiveId(c.student_id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 text-right border-b border-[#F2EADA] hover:bg-[#FAFAFA] ${
                activeId === c.student_id ? 'bg-amber-50' : ''
              }`}
              data-testid={`convo-${c.student_id}`}
            >
              <div className="w-10 h-10 rounded-full bg-[#EF9F27]/15 flex items-center justify-center shrink-0">
                <User className="w-5 h-5 text-[#EF9F27]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-[#412402] text-sm truncate">{c.student_name}</span>
                  {c.unread_count > 0 && (
                    <span className="bg-[#EF9F27] text-white text-[10px] rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center shrink-0">
                      {c.unread_count}
                    </span>
                  )}
                </div>
                <p className="text-xs text-[#412402]/50 truncate">{c.last_message || 'ابدأ المحادثة'}</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Thread */}
      <div className={`flex-1 flex flex-col ${activeId ? 'flex' : 'hidden md:flex'}`}>
        {!activeId ? (
          <div className="flex-1 flex flex-col items-center justify-center text-[#412402]/40">
            <MessageCircle className="w-14 h-14 mb-3 opacity-30" />
            <p className="text-sm">اختر محادثة لعرض الرسائل</p>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 px-4 py-3 border-b border-[#E8DFCF] bg-[#FFF8E7]">
              <button className="md:hidden text-[#412402]/60" onClick={() => setActiveId(null)}>‹ رجوع</button>
              <div className="w-9 h-9 rounded-full bg-[#EF9F27]/15 flex items-center justify-center">
                <User className="w-5 h-5 text-[#EF9F27]" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-[#412402] text-sm">{activeConvo?.student_name}</p>
                <p className="text-[11px] text-[#412402]/50">{activeConvo?.grade}{activeConvo?.section ? ` / ${activeConvo.section}` : ''}</p>
              </div>
              <Button
                size="sm" variant="ghost"
                onClick={deleteConversation}
                className="text-red-600 hover:bg-red-50 shrink-0"
                title="حذف المحادثة"
                data-testid="delete-conversation-btn"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2 bg-[#FCFAF5]">
              {messages.length === 0 ? (
                <p className="text-center text-xs text-[#412402]/40 py-10">لا توجد رسائل بعد — اكتب أول رسالة</p>
              ) : messages.map((m) => (
                <div key={m.id} className={`flex ${m.sender_role === 'teacher' ? 'justify-start' : 'justify-end'}`}>
                  <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${
                    m.sender_role === 'teacher'
                      ? 'bg-[#EF9F27] text-white rounded-br-sm'
                      : 'bg-white border border-[#E8DFCF] text-[#412402] rounded-bl-sm'
                  }`}>
                    <p className="whitespace-pre-wrap break-words">{m.text}</p>
                    <p className={`text-[10px] mt-0.5 ${m.sender_role === 'teacher' ? 'text-white/70' : 'text-[#412402]/40'}`}>
                      {new Date(m.created_at).toLocaleTimeString('ar-EG', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="p-3 border-t border-[#E8DFCF] flex items-center gap-2">
              <Input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="اكتب رسالة..."
                data-testid="teacher-msg-input"
              />
              <Button onClick={send} disabled={sending || !draft.trim()} className="bg-[#EF9F27] hover:bg-[#D88E1F] text-white shrink-0">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TeacherMessages;
